import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import AdminHeader from '../../../components/feature/AdminHeader';

interface KBMInsight {
  id: string;
  title: string;
  content: string;
  author: string;
  published_date: string;
  type: 'insight' | 'monthly_mission' | 'kbm_news';
  display_order: number;
  files?: { id: string; file_url: string; file_type: string }[];
}

export default function KBMInsightsAdmin() {
  const [insights, setInsights] = useState<KBMInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    published_date: new Date().toISOString().split('T')[0],
    type: 'kbm_news' as 'insight' | 'monthly_mission' | 'kbm_news',
    display_order: 0,
  });
  const [files, setFiles] = useState<File[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const { data: insightsData, error: insightsError } = await supabase
        .from('kbm_insights')
        .select('*')
        .order('display_order', { ascending: false });

      if (insightsError) throw insightsError;

      const insightsWithFiles = await Promise.all(
        (insightsData || []).map(async (insight) => {
          const { data: filesData } = await supabase
            .from('kbm_insights_files')
            .select('*')
            .eq('insight_id', insight.id);

          return { ...insight, files: filesData || [] };
        })
      );

      setInsights(insightsWithFiles);
    } catch (error) {
      console.error('Error fetching insights:', error);
      setMessage('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const uploadFiles = async (insightId: string) => {
    const uploadedUrls: { url: string; type: string }[] = [];

    for (const file of files) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${insightId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const fileType = file.type.includes('pdf') ? 'pdf' : 'image';

      const { error: uploadError } = await supabase.storage
        .from('kbm-insights')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('kbm-insights')
        .getPublicUrl(fileName);

      uploadedUrls.push({ url: urlData.publicUrl, type: fileType });
    }

    for (const { url, type } of uploadedUrls) {
      await supabase.from('kbm_insights_files').insert({
        insight_id: insightId,
        file_url: url,
        file_type: type,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    try {
      if (editingId) {
        const { error } = await supabase
          .from('kbm_insights')
          .update(formData)
          .eq('id', editingId);

        if (error) throw error;

        if (files.length > 0) {
          await uploadFiles(editingId);
        }

        setMessage('수정되었습니다.');
        setEditingId(null);
      } else {
        const { data, error } = await supabase
          .from('kbm_insights')
          .insert([formData])
          .select()
          .single();

        if (error) throw error;

        if (files.length > 0 && data) {
          await uploadFiles(data.id);
        }

        setMessage('추가되었습니다.');
      }

      setFormData({
        title: '',
        content: '',
        author: '',
        published_date: new Date().toISOString().split('T')[0],
        type: 'kbm_news',
        display_order: 0,
      });
      setFiles([]);
      fetchInsights();
    } catch (error) {
      console.error('Error:', error);
      setMessage('오류가 발생했습니다.');
    }
  };

  const handleEdit = (insight: KBMInsight) => {
    setFormData({
      title: insight.title,
      content: insight.content,
      author: insight.author,
      published_date: insight.published_date,
      type: insight.type,
      display_order: insight.display_order,
    });
    setEditingId(insight.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase.from('kbm_insights').delete().eq('id', id);
      if (error) throw error;

      setMessage('삭제되었습니다.');
      fetchInsights();
    } catch (error) {
      console.error('Error:', error);
      setMessage('삭제에 실패했습니다.');
    }
  };

  const handleDeleteFile = async (fileId: string, fileUrl: string) => {
    if (!confirm('파일을 삭제하시겠습니까?')) return;

    try {
      const filePath = fileUrl.split('/kbm-insights/')[1];
      await supabase.storage.from('kbm-insights').remove([filePath]);
      await supabase.from('kbm_insights_files').delete().eq('id', fileId);

      setMessage('파일이 삭제되었습니다.');
      fetchInsights();
    } catch (error) {
      console.error('Error:', error);
      setMessage('파일 삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center">
        <div className="text-lg text-gray-600">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <AdminHeader title="KBM Insights 관리" subtitle="칼럼과 월간 소식지를 관리합니다" />

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-12">
        {message && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-teal-50 border border-teal-200 rounded-lg text-teal-800 text-sm">
            {message}
          </div>
        )}

        {/* Form Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-8 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
            {editingId ? '수정하기' : '새 콘텐츠 추가'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                유형 <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'insight' | 'monthly_mission' | 'kbm_news' })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                required
              >
                <option value="kbm_news">킹덤브릿지 소식</option>
                <option value="insight">KBM Insights (칼럼)</option>
                <option value="monthly_mission">Monthly Mission (월간 소식지)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="제목을 입력하세요"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                내용 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                rows={5}
                placeholder="내용을 입력하세요"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  작성자 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="작성자 이름"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  발행일 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.published_date}
                  onChange={(e) => setFormData({ ...formData, published_date: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                표시 순서
              </label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="숫자가 클수록 먼저 표시됩니다"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                파일 업로드 (PDF 또는 이미지)
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                multiple
                accept="application/pdf,image/*"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
              <p className="mt-2 text-xs text-gray-500">
                여러 파일을 선택할 수 있습니다 (PDF, JPG, PNG 등)
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap"
              >
                {editingId ? '수정하기' : '추가하기'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setFormData({
                      title: '',
                      content: '',
                      author: '',
                      published_date: new Date().toISOString().split('T')[0],
                      type: 'kbm_news',
                      display_order: 0,
                    });
                    setFiles([]);
                  }}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium whitespace-nowrap"
                >
                  취소
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">등록된 콘텐츠</h2>

          {insights.length === 0 ? (
            <p className="text-center text-gray-500 py-8">등록된 콘텐츠가 없습니다.</p>
          ) : (
            <div className="space-y-4">
              {insights.map((insight) => (
                <div key={insight.id} className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          insight.type === 'insight' 
                            ? 'bg-teal-100 text-teal-700' 
                            : insight.type === 'kbm_news'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {insight.type === 'insight' ? 'KBM Insights' : insight.type === 'kbm_news' ? '킹덤브릿지 소식' : 'Monthly Mission'}
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500">{insight.published_date}</span>
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">{insight.title}</h3>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{insight.content}</p>
                      <p className="text-xs sm:text-sm text-gray-500">작성자: {insight.author}</p>
                    </div>
                    <div className="flex gap-2 self-end sm:self-start">
                      <button
                        onClick={() => handleEdit(insight)}
                        className="flex-1 sm:flex-none px-4 py-2 bg-teal-50 text-teal-600 rounded-lg hover:bg-teal-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDelete(insight.id)}
                        className="flex-1 sm:flex-none px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                      >
                        삭제
                      </button>
                    </div>
                  </div>

                  {insight.files && insight.files.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-3">첨부 파일:</p>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                        {insight.files.map((file) => (
                          <div key={file.id} className="relative group">
                            {file.file_type === 'pdf' ? (
                              <a
                                href={file.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center h-20 sm:h-24 bg-red-50 rounded-lg border border-red-200 hover:bg-red-100 transition-colors"
                              >
                                <i className="ri-file-pdf-line text-2xl sm:text-3xl text-red-600"></i>
                              </a>
                            ) : (
                              <img
                                src={file.file_url}
                                alt="첨부 이미지"
                                className="w-full h-20 sm:h-24 object-cover rounded-lg"
                              />
                            )}
                            <button
                              onClick={() => handleDeleteFile(file.id, file.file_url)}
                              className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity cursor-pointer"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
